import { Box, Button, IconButton } from "@mui/material"
import { IApiFunc, IApiResult } from "auth/interface/api.interface"
import { useApi } from "auth/useApi"
import { AppContext } from "components/AppContextProvider"
import ClpTable from "components/ClpTable"
import ContinueDialog from "components/Dialogs/ContinueDialog"
import { apiEndpoints } from "generated/apiEndpoints"
import { useContext, useEffect, useState } from "react"
import { ICustomerCustomRole } from "./customercustomrole.interface"
import CustomRoleForm from "./CustomRoleForm"
import { styled } from "@mui/material/styles";
import { ColumnDef } from "@tanstack/react-table";
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"

interface CustomRoleListProps {
  t?: any
  isSystemAdmin?: boolean;
  customerId?: number
}

const CustomRoleList = ({ isSystemAdmin, customerId, t }: CustomRoleListProps) => {
  const appContext = useContext(AppContext)
  const [roleResult, listRoleApi] = useApi() as [IApiResult, IApiFunc]
  const [addRoleResult, addRole] = useApi() as [IApiResult, IApiFunc]
  const [roleDetailsResult, roleDetails] = useApi() as [IApiResult, IApiFunc]
  const [deleteRoleResult, deleteRole] = useApi() as [IApiResult, IApiFunc]
  const [initialFormikValues, setInitialFormikValues] = useState<ICustomerCustomRole>({
    id: 0,
    name: ""
  })
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    selectedId: number
  }>({ open: false, selectedId: 0 })

  const handleSubmit = (values: ICustomerCustomRole) => {
    if (isSystemAdmin && customerId) {
      values.customerId = customerId
    }
    if (values.id > 0) {
      isSystemAdmin
        ? addRole.put(apiEndpoints.systemadmincustomers.updatecustomercustomrole, values)
        : addRole.put(apiEndpoints.customeradmin.updatecustomercustomrole, values)
    } else {
      isSystemAdmin
        ? addRole.post(apiEndpoints.systemadmincustomers.addcustomercustomrole, values)
        : addRole.post(apiEndpoints.customeradmin.addcustomercustomrole, values)
    }
  }

  const handleEdit = (id: number) => {
    isSystemAdmin
      ? roleDetails.get(
        apiEndpoints.systemadmincustomers.getcustomercustomrolebyid.replace("{id}", id.toString())
      )
      : roleDetails.get(apiEndpoints.customeradmin.getcustomercustomrolebyid.replace("{id}", id.toString()))
  }

  const handleDelete = () => {
    isSystemAdmin
      ? deleteRole.del(
        apiEndpoints.systemadmincustomers.deletecustomercustomrole.replace(
          "{id}",
          deleteDialog.selectedId.toString()
        )
      )
      : deleteRole.del(
        apiEndpoints.customeradmin.deletecustomercustomrole.replace(
          "{id}",
          deleteDialog.selectedId.toString()
        )
      )
  }
  const openDeleteDialog = (id: number) => {
    setDeleteDialog({ open: true, selectedId: id })
  }

  useEffect(() => {
    isSystemAdmin && customerId
      ? listRoleApi.get(
        apiEndpoints.systemadmincustomers.getcustomercustomroles.replace(
          "{customerid}",
          customerId.toString()
        )
      )
      : listRoleApi.get(apiEndpoints.customeradmin.getcustomercustomroles)
  }, [])

  useEffect(() => {
    if (addRoleResult.status === 2) {
      isSystemAdmin && customerId
        ? listRoleApi.get(
          apiEndpoints.systemadmincustomers.getcustomercustomroles.replace(
            "{customerid}",
            customerId.toString()
          )
        )
        : listRoleApi.get(apiEndpoints.customeradmin.getcustomercustomroles)
      appContext.toggleSideBar()
    }
  }, [addRoleResult])

  useEffect(() => {
    if (deleteRoleResult.status === 2) {
      isSystemAdmin && customerId
        ? listRoleApi.get(
          apiEndpoints.systemadmincustomers.getcustomercustomroles.replace(
            "{customerid}",
            customerId.toString()
          )
        )
        : listRoleApi.get(apiEndpoints.customeradmin.getcustomercustomroles)
      setDeleteDialog({ open: false, selectedId: 0 })
    }
  }, [deleteRoleResult])

  useEffect(() => {
    if (roleDetailsResult.status === 2) {
      setInitialFormikValues(roleDetailsResult.data)

      !appContext.isSidebarOpen && appContext.toggleSideBar(true)
    }
  }, [roleDetailsResult])

  useEffect(() => {
    if (!appContext.isSidebarOpen) {
      setInitialFormikValues({
        id: 0,
        name: ""
      })
    }
  }, [appContext.isSidebarOpen])

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: "name",
      header: t("global.role", "Role")
    },
    {
      accessorKey: "edit",
      header: "",
      meta: {
        align: "right"
      },
      cell: ({ cell }: any) => {
        return (
          <StyledTableButtonGroup>
            <TableButton
              type={TableButtonTypeEnum.Edit}
              handleOnClick={() => {
                handleEdit(cell.row.original.id)
              }}
            />
            <TableButton
              type={TableButtonTypeEnum.Delete}
              handleOnClick={() => {
                openDeleteDialog(cell.row.original.id)
              }}
            />
          </StyledTableButtonGroup>
        )
      }
    }
  ]

  return (
    <>
      <CustomRoleForm
        t={t}
        initialFormikValues={initialFormikValues}
        setInitialFormikValues={setInitialFormikValues}
        handleSubmit={handleSubmit}
        results={addRoleResult}
      />

      <ContinueDialog
        onAddSubmit={handleDelete}
        message={t("settings.customer.do_you_want_to_remove_role", "Do you want to delete role?")}
        continueBtnText={t("global.remove_role", "Delete role")}
        cancelBtnText={t("global.cancel", "Cancel")}
        result={deleteRoleResult}
        setShowContinueModal={(value: any) => setDeleteDialog({ open: value, selectedId: 0 })}
        isOpen={deleteDialog.open}
      />

      <Box px={{ xs: 2, sm: 3 }} pt={{ xs: 2, sm: 3 }}>
        <Box display="flex" flexDirection="row" justifyContent="flex-end">
          <Box order={3}>
            <Button onClick={() => appContext.toggleSideBar()} variant="contained" color="primary">
              {t("global.add_role", "Add role")}
            </Button>
          </Box>
        </Box>
      </Box>
      <Box>
        <ClpTable
          rows={roleResult.status === 2 && roleResult.data}
          loadingState={roleResult.status}
          columns={columns}
        />
      </Box>
    </>
  )
}

export default CustomRoleList
